import {
    Button,
    ButtonText,
    FormField,
    HStack,
    Input,
    InputField,
    SelectGeneric,
    useNumberInputRules,
    VStack
} from "@hosttools/frontend";
import type React from "react";
import {memo} from "react";
import {Controller, useForm} from "react-hook-form";

import {Card, CardBody, CardFooter} from "../Card";

import {taxTypes} from "./constant";
import type {Tax} from "./types";

type BaseProps = {
    tax: Tax | undefined;
    onSave: (item: Tax) => void;
    onCancel: () => void;
};

interface PropsWithPercent extends BaseProps {
    type: "percent";
}

interface PropsWithFlat extends BaseProps {
    type: "flat";
    currency: string;
}

type Props = StrictUnion<PropsWithPercent | PropsWithFlat>;

const TaxForm: React.FC<Props> = ({tax, currency, type, onSave, onCancel}) => {
    const {
        control,
        register,
        handleSubmit,
        formState: {errors}
    } = useForm<Tax>({
        defaultValues: tax
    });

    const inputNumberRules = useNumberInputRules();

    return (
        <Card>
            <CardBody>
                <VStack className="lg:flex-row" space="lg">
                    <FormField error={errors?.name?.message} label="Tax name">
                        <Input isRequired>
                            <InputField
                                id="name"
                                placeholder="Tax name..."
                                testID="name"
                                type="text"
                                {...register("name", {
                                    required: "This field is required"
                                })}
                            />
                        </Input>
                    </FormField>
                    <FormField error={errors?.type?.message} label="Tax type">
                        <Controller
                            control={control}
                            name="type"
                            render={({field}) => {
                                return (
                                    <SelectGeneric
                                        options={taxTypes}
                                        placeholder="Select type..."
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                );
                            }}
                            rules={{required: "This field is required"}}
                        />
                    </FormField>
                    <FormField
                        error={errors.value?.message}
                        label={type === "flat" ? "Amount" : "Percent"}
                    >
                        <Input
                            isRequired
                            leftElement={type === "flat" ? currency : undefined}
                            rightElement={type === "percent" ? "%" : undefined}
                        >
                            <InputField
                                placeholder={type === "flat" ? "Amount..." : "Percent..."}
                                testID="value"
                                type="number"
                                {...register("value", {
                                    ...inputNumberRules,
                                    required: "This field is required"
                                })}
                            />
                        </Input>
                    </FormField>
                </VStack>
            </CardBody>
            <CardFooter>
                <HStack className="justify-end" space="sm">
                    <Button variant="ghost" onPress={onCancel}>
                        <ButtonText>Cancel</ButtonText>
                    </Button>
                    <Button onPress={handleSubmit(onSave)}>
                        <ButtonText>Save</ButtonText>
                    </Button>
                </HStack>
            </CardFooter>
        </Card>
    );
};

export default memo(TaxForm);
